import React from 'react';
import { imageService } from '../lib/imageService';

/**
 * Reusable Image Display Component
 * Features: Caching, fallback images, loading states
 * 
 * @param {Object} props
 * @param {string} props.imageUrl - URL of the image to display
 * @param {string} props.entityType - Type of entity (organization, center, class, user)
 * @param {string} props.alt - Alt text for the image
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.size - Size preset (sm, md, lg, xl)
 * @param {boolean} props.rounded - Apply rounded corners
 * @param {Function} props.onClick - Click handler
 */
const ImageDisplay = ({
    imageUrl,
    entityType,
    alt,
    className = '',
    size = 'md',
    rounded = true,
    onClick
}) => {
    const [displayUrl, setDisplayUrl] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [hasError, setHasError] = React.useState(false);

    // Size presets
    const sizeClasses = {
        sm: 'w-8 h-8',
        md: 'w-10 h-10',
        lg: 'w-16 h-16',
        xl: 'w-24 h-24'
    };

    React.useEffect(() => {
        let mounted = true;

        // Get fallback image URL
        const getFallbackUrl = () => {
            return `/images/defaults/${entityType}.svg`;
        };

        const loadImage = async () => {
            setIsLoading(true);
            setHasError(false);

            try {
                let urlToUse = imageUrl;

                // Try to get cached image first
                if (imageUrl) {
                    const cached = await imageService.getCachedImage(imageUrl);
                    if (cached) {
                        urlToUse = cached;
                    }
                }

                // If no image URL provided, use fallback
                if (!urlToUse) {
                    urlToUse = getFallbackUrl();
                }

                // Test if image loads successfully
                if (urlToUse && mounted) {
                    const img = new Image();
                    img.onload = () => {
                        if (mounted) {
                            setDisplayUrl(urlToUse);
                            setIsLoading(false);
                        }
                    };
                    img.onerror = () => {
                        if (mounted) {
                            // If main image fails, try fallback
                            if (urlToUse !== getFallbackUrl()) {
                                setDisplayUrl(getFallbackUrl());
                            } else {
                                setHasError(true);
                            }
                            setIsLoading(false);
                        }
                    };
                    img.src = urlToUse;
                } else if (mounted) {
                    setDisplayUrl(getFallbackUrl());
                    setIsLoading(false);
                }
            } catch (error) {
                if (mounted) {
                    setDisplayUrl(getFallbackUrl());
                    setIsLoading(false);
                }
            }
        };

        loadImage();

        return () => {
            mounted = false;
        };
    }, [imageUrl, entityType]);

    // Loading state
    if (isLoading) {
        return (
            <div
                className={`
          ${sizeClasses[size]} 
          ${rounded ? 'rounded-lg' : ''} 
          bg-gray-200 animate-pulse 
          ${className}
        `}
            >
                <div className="w-full h-full bg-gray-300 rounded-lg"></div>
            </div>
        );
    }

    // Error state (when even fallback fails)
    if (hasError) {
        return (
            <div
                className={`
          ${sizeClasses[size]} 
          ${rounded ? 'rounded-lg' : ''} 
          bg-gray-100 border border-gray-300 flex items-center justify-center text-gray-400
          ${className}
        `}
            >
                <svg className="w-1/2 h-1/2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
            </div>
        );
    }

    // Success state
    return (
        <img
            src={displayUrl}
            alt={alt || `${entityType} image`}
            className={`
        ${sizeClasses[size]} 
        ${rounded ? 'rounded-lg' : ''} 
        object-cover
        ${onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}
        ${className}
      `}
            onClick={onClick}
            loading="lazy"
        />
    );
};

export default ImageDisplay;